import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRouteMatch } from 'react-router';
import { Button, Tag, Divider, Modal, Space } from 'antd';

import {
  useDeleteQuotationItem,
  useQuotationItems,
} from '@/core/domain/stock/Quotations/Quotations.hooks';
import { QuotationItem } from '@/core/domain/stock/Quotations/Quotations.types';

import Table, { ColumnsType } from '@/components/_common/Table';
import CreateQuotationItemModal from '@/components/Movements/Quotations/QuotationItems/CreateQuotationItemModal';
import UpdateQuotationItemModal from '@/components/Movements/Quotations/QuotationItems/UpdateQuotationItemModal';
import { COLUMNS_WIDTH, getTableScrollX } from '@/utils/table';
import { useQuotationContext } from '..';
import { DeleteIcon, EditIcon } from '@/components/_common';
import useConfigurationColumnsPart from '@/hooks/useConfigurationColumnsPart';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import formatterPrice from '@/utils/number/formatterPrice';
import { StockItemType } from '@/core/domain/stock/StockItems/StockItems.types';

const QuotationDetailsItemsPage = () => {
  const intl = useIntl();
  const { quotationId } = useRouteMatch<{ quotationId: string }>().params;
  const quotation = useQuotationContext();

  const [createQuotationItemModalVisible, setCreateQuotationItemModalVisible] =
    useState(false);

  const [updateQuotationItemModalVisible, setUpdateQuotationItemModalVisible] =
    useState(false);

  const [selectedQuotationItem, setSelectedQuotationItem] =
    useState<QuotationItem>();

  const { data: quotationItems, isFetching } = useQuotationItems(+quotationId);

  const deleteMutation = useDeleteQuotationItem(+quotationId);

  const handleCreateQuotationItemModalVisible = () => {
    setCreateQuotationItemModalVisible(!createQuotationItemModalVisible);
  };

  const handleUpdateQuotationItemModalVisible = (
    quotationItem?: QuotationItem,
  ) => {
    if (updateQuotationItemModalVisible) {
      setSelectedQuotationItem(undefined);
    } else {
      setSelectedQuotationItem(quotationItem);
    }

    setUpdateQuotationItemModalVisible(!updateQuotationItemModalVisible);
  };

  const handleRemoveQuotationItemClick = (quotationItem: QuotationItem) => {
    Modal.confirm({
      title: intl.formatMessage({ id: 'quotations.details.items.remove' }),
      content: `${intl.formatMessage({
        id: 'quotations.details.items.remove.alert',
      })} ${quotationItem?.name}`,
      onOk: async () => {
        const obj = { quotationId, quotationItemId: quotationItem.id };

        await deleteMutation.mutateAsync(obj.quotationItemId);
      },
      okType: 'danger',
    });
  };

  const columns: ColumnsType<QuotationItem> = [
    {
      dataIndex: 'workOrderNumber',
      width: COLUMNS_WIDTH.default,
      ellipsis: true,
      title: 'Orçamento',
      render: (value) => value ?? '-',
    },
    useConfigurationColumnsPart({ internalCode: 'sku' }),
    {
      dataIndex: 'name',
      width: COLUMNS_WIDTH.default,
      ellipsis: true,
      title: intl.formatMessage({
        id: 'quotations.details.items.name',
      }),
    },
    {
      dataIndex: ['manufacturer', 'name'],
      width: COLUMNS_WIDTH.default,
      ellipsis: true,
      title: intl.formatMessage({
        id: 'quotations.details.items.manufacturer',
      }),
    },
    {
      dataIndex: 'type',
      width: COLUMNS_WIDTH.default,
      ellipsis: true,
      title: intl.formatMessage({
        id: 'quotations.details.items.type',
      }),
      render: (value) => (
        <Tag color="processing">
          <FormattedMessage
            id={`quotations.domain.enums.item_type.${
              value === StockItemType.Part ? 'part' : 'consumable'
            }`}
          />
        </Tag>
      ),
    },
    {
      dataIndex: 'quantity',
      width: COLUMNS_WIDTH.small,
      title: intl.formatMessage({
        id: 'quotations.details.items.quantity',
      }),
      render: (value) => formatterPrice(value),
    },
  ];

  if (!quotation?.disabled) {
    columns.push({
      title: intl.formatMessage({ id: 'commons.list.header.action' }),
      width: 100,
      fixed: 'right',
      align: 'right',
      render: (row) => (
        <Space>
          <EditIcon
            iconProps={{
              onClick: () => handleUpdateQuotationItemModalVisible(row),
            }}
          />

          <Divider type="vertical" />

          <DeleteIcon onClick={() => handleRemoveQuotationItemClick(row)} />
        </Space>
      ),
    });
  }

  return (
    <>
      <div>
        <SpaceForButton justifyContent="space-between">
          <h2>
            <FormattedMessage id="quotations.details.items" />
          </h2>

          <div>
            <Button
              block
              type="primary"
              onClick={handleCreateQuotationItemModalVisible}
              disabled={quotation?.disabled}
            >
              <FormattedMessage id="quotations.details.items.create" />
            </Button>
          </div>
        </SpaceForButton>

        <Table<QuotationItem>
          loading={isFetching}
          rowKey={(row) => row.id}
          dataSource={quotationItems?.result?.data}
          columns={columns}
          scroll={{ x: getTableScrollX(columns) }}
        />
      </div>

      <CreateQuotationItemModal
        visible={createQuotationItemModalVisible}
        handleVisible={handleCreateQuotationItemModalVisible}
      />

      <UpdateQuotationItemModal
        visible={updateQuotationItemModalVisible}
        handleVisible={handleUpdateQuotationItemModalVisible}
        quotationItem={selectedQuotationItem}
      />
    </>
  );
};

export default QuotationDetailsItemsPage;
